// features/projects/project-thread-bar.js
// ═══════════════════════════════════════════════════════════
// Sits at the top of the project chat panel. Two actions:
//   + New Thread — spawns a fresh chat thread under the same
//                  project workspace (canvas, journey map, artifacts
//                  all stay the same — only the chat thread is new)
//   History     — dropdown list of all chat threads for this
//                 project workspace, click to switch
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';

export default function ProjectThreadBar({
  userId,
  workspaceId,
  activeThreadId,
  onSwitchThread,
}) {
  const [threads, setThreads] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  const loadThreads = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await fetch(
        `/api/project-threads?workspace_id=${encodeURIComponent(workspaceId)}`,
        { cache: 'no-store' }
      );
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads || []);
      }
    } catch (err) {
      console.warn('[project-threads] load failed:', err.message);
    }
  }, [workspaceId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showHistory) return;
    function onDoc(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowHistory(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showHistory]);

  async function handleNewThread() {
    if (creating) return;
    setCreating(true);
    try {
      const res = await fetch('/api/project-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.warn('[project-threads] create failed:', data.error);
        setCreating(false);
        return;
      }
      // Switch to the new thread immediately
      onSwitchThread?.(data.thread.id);
      await loadThreads();
    } catch (err) {
      console.warn('[project-threads] create failed:', err.message);
    } finally {
      setCreating(false);
    }
  }

  function handleSwitch(threadId) {
    setShowHistory(false);
    onSwitchThread?.(threadId);
  }

  async function handleRename(threadId, newTitle) {
    try {
      const res = await fetch('/api/project-threads', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: threadId, title: newTitle }),
      });
      if (res.ok) await loadThreads();
    } catch (err) {
      console.warn('[project-threads] rename failed:', err.message);
    }
  }

  async function handleDelete(threadId) {
    const ok = window.confirm(
      'Delete this thread? Its chat messages will be removed. The canvas + journey map are not affected.'
    );
    if (!ok) return;
    try {
      const res = await fetch(`/api/project-threads?id=${encodeURIComponent(threadId)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        // If we deleted the active thread, switch back to default
        if (threadId === activeThreadId) onSwitchThread?.(workspaceId);
        await loadThreads();
      }
    } catch (err) {
      console.warn('[project-threads] delete failed:', err.message);
    }
  }

  // Find the active thread label (or "Default thread" if active = workspace_id)
  const isDefaultActive = !activeThreadId || activeThreadId === workspaceId;
  const activeThread = threads.find(t => t.id === activeThreadId);
  const activeLabel = activeThread?.title
    || (isDefaultActive ? 'Default thread' : 'New thread');

  return (
    <div style={S.bar}>
      <button
        style={S.btn}
        onClick={handleNewThread}
        disabled={creating}
        title="Spawn a fresh chat thread — keeps your canvas + artifacts"
      >
        <span style={S.btnPlus}>+</span>
        New Thread
      </button>

      <div style={S.divider} />

      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          style={{ ...S.btn, ...S.historyBtn }}
          onClick={() => setShowHistory(s => !s)}
          title="Browse past threads in this project"
        >
          <span style={S.historyIcon}>◷</span>
          History
          <span style={{ marginLeft: 6, color: '#555', fontSize: 10 }}>
            ({threads.length || 0})
          </span>
        </button>

        {showHistory && (
          <div style={S.dropdown}>
            <div style={S.dropdownHeader}>Chat threads</div>

            {/* Default thread is always present (id = workspace_id) */}
            <button
              style={{
                ...S.dropdownItem,
                ...(isDefaultActive ? S.dropdownItemActive : {}),
              }}
              onClick={() => handleSwitch(workspaceId)}
            >
              <span style={S.dropdownItemDot}>◈</span>
              <div style={S.dropdownItemBody}>
                <div style={S.dropdownItemTitle}>Default thread</div>
                <div style={S.dropdownItemMeta}>The thread this project started with</div>
              </div>
            </button>

            {threads.length === 0 && (
              <div style={S.dropdownEmpty}>
                No additional threads yet. Click + New Thread to start one.
              </div>
            )}

            {threads.map(t => (
              <ThreadRow
                key={t.id}
                thread={t}
                isActive={t.id === activeThreadId}
                onSwitch={() => handleSwitch(t.id)}
                onRename={(newTitle) => handleRename(t.id, newTitle)}
                onDelete={() => handleDelete(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div style={S.activeIndicator}>
        <span style={S.activeIndicatorLabel}>Now in:</span>
        <span style={S.activeIndicatorValue}>{activeLabel}</span>
      </div>
    </div>
  );
}

// ─── Thread row with hover-to-reveal Rename + Delete ────────

function ThreadRow({ thread, isActive, onSwitch, onRename, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thread.title || '');

  const displayTitle = thread.title
    || `Thread ${new Date(thread.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  function startRename(e) {
    e.stopPropagation();
    setDraft(thread.title || '');
    setEditing(true);
  }

  function commitRename() {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed !== (thread.title || '')) {
      onRename(trimmed);
    }
  }

  function cancelRename() {
    setEditing(false);
    setDraft(thread.title || '');
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...S.dropdownItem,
        ...(isActive ? S.dropdownItemActive : {}),
        position: 'relative',
        cursor: editing ? 'text' : 'pointer',
      }}
      onClick={editing ? undefined : onSwitch}
    >
      <span style={S.dropdownItemDot}>◇</span>
      <div style={S.dropdownItemBody}>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
            }}
            onBlur={commitRename}
            placeholder="Thread name"
            style={S.renameInput}
          />
        ) : (
          <div style={S.dropdownItemTitle}>{displayTitle}</div>
        )}
        <div style={S.dropdownItemMeta}>
          {new Date(thread.last_activity_at).toLocaleString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
          })}
        </div>
      </div>

      {hovered && !editing && (
        <div style={S.rowActions}>
          <button
            style={S.rowActionBtn}
            onClick={startRename}
            title="Rename thread"
            aria-label="Rename"
          >
            ✎
          </button>
          <button
            style={{ ...S.rowActionBtn, color: '#c45050' }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete thread"
            aria-label="Delete"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

const S = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    background: '#0c0c11',
    borderBottom: '1px solid #1e1e2a',
    flexShrink: 0,
    fontFamily: 'Georgia, serif',
  },
  btn: {
    background: 'transparent',
    border: '1px solid rgba(122,158,130,0.3)',
    color: '#7a9e82',
    padding: '5px 12px',
    borderRadius: 4,
    fontSize: 11,
    fontFamily: 'Georgia, serif',
    cursor: 'pointer',
    letterSpacing: '0.04em',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  },
  btnPlus: { fontSize: 13, lineHeight: 1 },
  historyBtn: {
    borderColor: 'rgba(155,143,196,0.3)',
    color: '#9b8fc4',
  },
  historyIcon: { fontSize: 11, lineHeight: 1 },
  divider: {
    width: 1, height: 16, background: '#1e1e2a',
  },
  activeIndicator: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    fontSize: 11,
    fontFamily: 'Georgia, serif',
  },
  activeIndicatorLabel: { color: '#555' },
  activeIndicatorValue: { color: '#7a9e82', fontStyle: 'italic' },

  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    minWidth: 280,
    maxWidth: 340,
    maxHeight: 400,
    overflowY: 'auto',
    background: '#111118',
    border: '1px solid #3a3a52',
    borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    zIndex: 50,
    padding: 6,
  },
  dropdownHeader: {
    fontSize: 9,
    color: '#555',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    padding: '6px 10px 8px',
    fontFamily: '"DM Mono", monospace',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    background: 'none',
    border: 'none',
    padding: '8px 10px',
    borderRadius: 5,
    color: '#b8c8bb',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'Georgia, serif',
    transition: 'background 0.12s',
  },
  dropdownItemActive: {
    background: 'rgba(122,158,130,0.08)',
    color: '#7a9e82',
  },
  dropdownItemDot: {
    color: '#7a9e82',
    fontSize: 14,
    marginTop: 1,
  },
  dropdownItemBody: { flex: 1, minWidth: 0 },
  dropdownItemTitle: {
    fontSize: 13,
    color: 'inherit',
    marginBottom: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownItemMeta: {
    fontSize: 10,
    color: '#555',
    fontFamily: '"DM Mono", monospace',
    letterSpacing: '0.05em',
  },
  dropdownEmpty: {
    padding: '12px 10px',
    fontSize: 11,
    color: '#555',
    fontStyle: 'italic',
  },
  renameInput: {
    width: '100%',
    background: 'rgba(122,158,130,0.06)',
    border: '1px solid rgba(122,158,130,0.4)',
    borderRadius: 4,
    padding: '4px 8px',
    color: '#e8e8f0',
    fontSize: 13,
    fontFamily: 'Georgia, serif',
    outline: 'none',
    marginBottom: 4,
  },
  rowActions: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    gap: 4,
    background: '#111118',
    padding: '2px',
    borderRadius: 4,
    border: '1px solid #272736',
  },
  rowActionBtn: {
    background: 'none',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    fontSize: 11,
    padding: '3px 7px',
    borderRadius: 3,
    fontFamily: 'inherit',
    lineHeight: 1,
    transition: 'background 0.12s',
  },
};
